import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const FeaturedArticlesManager = () => {
  const [allArticles, setAllArticles] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all published articles
      const articlesRes = await axios.get(`${API}/articles`);
      setAllArticles(articlesRes.data.filter(a => a.published));

      // Fetch current featured articles
      const featuredRes = await axios.get(`${API}/articles/featured`);
      setFeaturedIds(featuredRes.data.map(a => a.id));
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = (articleId) => {
    setFeaturedIds(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      } else {
        if (prev.length >= 6) {
          alert('Maximum 6 featured articles allowed');
          return prev;
        }
        return [...prev, articleId];
      }
    });
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newIds = [...featuredIds];
    [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
    setFeaturedIds(newIds);
  };

  const moveDown = (index) => {
    if (index === featuredIds.length - 1) return;
    const newIds = [...featuredIds];
    [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
    setFeaturedIds(newIds);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/articles/featured/update`,
        featuredIds,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('Featured articles updated successfully!');
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save featured articles');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const featuredArticles = featuredIds
    .map(id => allArticles.find(a => a.id === id))
    .filter(Boolean);

  const availableArticles = allArticles.filter(
    a => !featuredIds.includes(a.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light mb-2">Manage Kasango Journal Featured Articles</h2>
          <p className="text-sm text-gray-500">
            Select up to 6 articles to display in the Kasango Journal section ({featuredIds.length}/6)
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-white text-black hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'SAVE CHANGES'}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Featured Articles - Left Column */}
        <div>
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Featured Articles ({featuredIds.length})
          </h3>
          
          {featuredArticles.length === 0 ? (
            <div className="border border-dashed border-gray-700 rounded-lg p-8 text-center text-gray-500">
              No featured articles selected. Select from available articles →
            </div>
          ) : (
            <div className="space-y-3">
              {featuredArticles.map((article, index) => (
                <div
                  key={article.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-start gap-3"
                >
                  {/* Order Controls */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="w-6 h-6 flex items-center justify-center hover:bg-zinc-800 rounded disabled:opacity-30"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <div className="w-6 h-6 flex items-center justify-center text-xs font-bold bg-green-500 text-black rounded">
                      {index + 1}
                    </div>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === featuredArticles.length - 1}
                      className="w-6 h-6 flex items-center justify-center hover:bg-zinc-800 rounded disabled:opacity-30"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>

                  {/* Article Info */}
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1 line-clamp-2">{article.title}</h4>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-zinc-800 rounded">
                        {article.category}
                      </span>
                      <span>{article.view_count || 0} views</span>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => toggleFeatured(article.id)}
                    className="text-red-500 hover:text-red-400 p-1"
                    title="Remove from featured"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Articles - Right Column */}
        <div>
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gray-500"></span>
            Available Articles ({availableArticles.length})
          </h3>
          
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {availableArticles.map(article => (
              <div
                key={article.id}
                className="bg-zinc-950 border border-zinc-800 hover:border-zinc-700 rounded-lg p-3 flex items-start gap-3 cursor-pointer transition-colors"
                onClick={() => toggleFeatured(article.id)}
              >
                <div className="flex-1">
                  <h4 className="font-light text-sm mb-1 line-clamp-2">{article.title}</h4>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-0.5 bg-zinc-900 rounded">
                      {article.category}
                    </span>
                    <span>{article.view_count || 0} views</span>
                  </div>
                </div>

                {/* Add Button */}
                <button
                  className="text-green-500 hover:text-green-400 p-1"
                  title="Add to featured"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedArticlesManager;
